/** @jsx jsx */
import { jsx, InterpolationWithTheme } from '@emotion/core';
import React, { useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectInstantCredit, Wallet } from 'state/instantCredit';
import useIdiSdk from 'hooks/useIdiSdk';
import useTranslate from 'hooks/useTranslate';
import PermanentWalletButton, {
  usePermanentWalletButton,
} from 'components/PermanentWalletButton';
import Button from 'components/Button';
import Link from 'components/Link';
import styles from './styles';
import { test, any } from 'ramda';
import { Theme } from 'features/Theme';
import { CircularProgress } from '@material-ui/core';
import isPresent from 'utils/isPresent';

interface PropTypes {
  containerStyles: InterpolationWithTheme<Theme>;
  buttonContainerStyle: InterpolationWithTheme<Theme>;
}

const isAppleWallet = (wallet: Wallet) =>
  test(/apple_pay/i, wallet.consumerFacingEntityName);

const PermanentWallets: React.FC<PropTypes> = props => {
  const permanentWalletButton = usePermanentWalletButton();
  const { containerStyles, buttonContainerStyle } = props;
  const { wallets, firstName, lastName } = useSelector(selectInstantCredit);
  const t = useTranslate();
  const { initialize, pushProvision, isReady, inProgress } = useIdiSdk();

  const appleButtonId = 'appleButton';
  const hasPermanentAppleButton = useMemo(() => {
    if (!wallets) {
      return false;
    }

    return any(wallet => isAppleWallet(wallet), wallets.onDeviceWallets);
  }, [wallets]);

  useEffect(() => {
    if (!isReady && firstName !== undefined && lastName !== undefined) {
      initialize({
        firstName: firstName,
        lastName: lastName,
        appleButtonId: hasPermanentAppleButton ? appleButtonId : undefined,
      });
    }
  }, [firstName, hasPermanentAppleButton, initialize, isReady, lastName]);

  const handleClick = useCallback(
    async (wallet: Wallet) => {
      if (inProgress) {
        return;
      }
      pushProvision(wallet);
    },
    [inProgress, pushProvision],
  );

  if (
    wallets === undefined ||
    wallets.onDeviceWallets === [] ||
    firstName === undefined ||
    lastName === undefined
  ) {
    return null;
  }

  const isLoading = !isReady || inProgress;

  return (
    <React.Fragment>
      {isLoading ? <CircularProgress aria-label="progress" size={24} /> : null}
      <div css={{ display: isLoading ? 'none' : 'unset' }}>
        <div css={containerStyles}>
          {wallets.onDeviceWallets.map((wallet: Wallet) => {
            const displayed =
              (isAppleWallet(wallet) &&
                permanentWalletButton.canDisplayAppleButton) ||
              (!isAppleWallet(wallet) &&
                permanentWalletButton.canDisplayNonAppleButton);
            return (
              <div
                key={wallet.walletId}
                css={buttonContainerStyle}
                style={{
                  display: displayed ? 'initial' : 'none',
                }}
              >
                <PermanentWalletButton
                  id={isAppleWallet(wallet) ? appleButtonId : wallet.walletId}
                  disabled={!displayed}
                  css={styles.walletButton}
                  wallet={wallet}
                  onClick={handleClick}
                />
              </div>
            );
          })}
        </div>

        <div css={containerStyles}>
          {wallets && isPresent(wallets.eligibleOnFileWallets) ? (
            <Button
              id="merchantsButton"
              variant="text"
              component={Link}
              to={'/add-merchants'}
            >
              {t('dsp.permanentWallets.addToMerchantsButton')}
            </Button>
          ) : null}
        </div>
      </div>
    </React.Fragment>
  );
};

export default PermanentWallets;
